import { Stack, Text } from '@grovus/design-system'
import type { Dict } from '../i18n'
import type { CreativesResponse } from './contract'
import { fill } from './format'

/**
 * What the figures on screen cannot see (D8), stated once above the list or the table
 * whatever the filters leave, and in place of an empty state. One line per cause, so an
 * unplaced ad and an unread day both show when both hold. The exclusion line gives each
 * window's count, because the basis holds when either window has one. Under them, one line
 * per reason the unplaced and excluded ads carry (GRO-156 D3); the lines above stay. A
 * `placeholder_image` group can be the only line, because those ads are settled (GRO-195 D8).
 */
export function ScopeNotices({ response, t }: { response: CreativesResponse; t: Dict }) {
  const { scope, comparison } = response
  const lines: Array<{ key: string; text: string }> = []
  if (scope.unplacedAds > 0) lines.push({ key: 'unplaced', text: t.scopeUnplaced })
  if (scope.uncoveredDays > 0) lines.push({ key: 'uncovered', text: t.scopeUncovered })
  if (scope.basis === 'identifiedAds') {
    const counts = comparison === null
      ? fill(t.scopeExcludedCount, { c: scope.excludedAds })
      : fill(t.scopeExcludedCompared, { c: scope.excludedAds, p: comparison.period.excludedAds })
    lines.push({ key: 'excluded', text: `${t.scopeExcluded} ${counts}` })
  }
  const labels: Record<string, string> = t.unplacedReasons
  for (const group of scope.unplacedReasons) {
    const vars = { r: labels[group.reason] ?? group.reason, c: group.ads }
    lines.push({
      key: `reason-${group.reason}${group.excluded ? '-excluded' : ''}`,
      text: fill(group.excluded ? t.scopeReasonExcluded : t.scopeReason, vars),
    })
  }
  if (lines.length === 0) return null
  return (
    <Stack gap={4} data-testid="scope-notices">
      {lines.map((line) => (
        <Text key={line.key} size="sm" data-testid={`scope-${line.key}`}>
          {line.text}
        </Text>
      ))}
    </Stack>
  )
}
